import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, AlertCircle, X, ChevronLeft, ChevronRight, Layers,
  Image as ImageIcon, MessageSquare, User, Eye, CheckCircle,
  Trash2, RotateCcw, ScanLine, Sparkles, ThumbsUp, Gauge
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AnnotatedImage, { colorForTag } from '@/components/reviews/AnnotatedImage';
import config from '@/config';

// Frames parked by a final review, grouped by upc exactly like the review queue,
// so admins see the same unit of work the pipeline acted on.
//
// Two gates park frames here and they read differently: the human reviewer marks
// areas on the image, the AI reviewer judges the whole image and reports a code
// with a severity and a confidence. Both are shown side by side, each tagged with
// who raised the flag, and both offer the same ways out.

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(value).slice(0, 19);
  }
};

// AI findings have no fixed tag vocabulary, so they get one colour of their own
// rather than falling through to the annotator's grey default.
const AI_COLOR = '#7c3aed';
const colorForReason = (tag, source) => (source === 'ai' ? AI_COLOR : colorForTag(tag));

const SOURCES = {
  human: {
    label: 'Human',
    Icon: User,
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
    blurb: 'Rejected by a reviewer in final human verification.',
  },
  ai: {
    label: 'AI',
    Icon: Sparkles,
    chip: 'bg-violet-50 text-violet-700 border-violet-200',
    blurb: 'Flagged by the automated final review, before it reached a human.',
  },
};

function SourceBadge({ source, size = 'sm' }) {
  const meta = SOURCES[source];
  if (!meta) return null;
  const { Icon, label, chip } = meta;
  return (
    <span
      title={meta.blurb}
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${chip} ${
        size === 'xs' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
      }`}
    >
      <Icon className={size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
      {label}
    </span>
  );
}

function ReasonChip({ tag, count, source }) {
  const color = colorForReason(tag, source);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: `${color}1a`, color }}
    >
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      {tag}
      {count > 1 && <span className="opacity-70">×{count}</span>}
    </span>
  );
}

/**
 * Keyboard shortcuts, one per layer of the page:
 *
 *   list      E        open the first flagged frame
 *   viewer    Shift+E  accept every flagged image on the frame
 *   confirm   E        confirm
 *   result    E/Enter  dismiss, back to the list
 *
 * Only the topmost layer listens, so the same key can mean different things
 * without the layers fighting each other.
 */
const ACCEPT_KEY = 'e';

/** True when the key belongs to whatever the user is typing in. */
function isTypingTarget(target) {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/** A plain, un-modified press of `key` that is safe to act on. */
function isShortcut(event, key, { shift = false } = {}) {
  if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.shiftKey !== shift) return false;
  if (isTypingTarget(event.target)) return false;
  return event.key.toLowerCase() === key;
}

/** Small visual hint so the shortcuts are discoverable. */
function Kbd({ children }) {
  return (
    <kbd className="px-1.5 py-0.5 rounded border border-current/30 bg-white/60 font-sans text-[10px] font-semibold leading-none">
      {children}
    </kbd>
  );
}

// The two ways to send a flagged frame back. Both drop the per-colour output
// workflows; they differ in where the frame re-enters the pipeline. Accepting the
// image is the third way out and is handled separately - it touches one image,
// not the whole frame.
const ACTIONS = {
  frame_recheck: {
    label: 'Send to frame check',
    icon: RotateCcw,
    blurb: 'Deletes the output workflows and marks the frame as not completed, so it returns to the frame check page for a human to fix.',
  },
  segmentation_recheck: {
    label: 'Send to segmentation review',
    icon: ScanLine,
    blurb: 'Deletes the output workflows and sends the frame back to Segmentation Revision for both lens branches, re-running everything downstream. Existing mask corrections are kept.',
  },
};

/**
 * Confirmation for a remediation action.
 *
 * Loads the server's plan first and shows exactly what will change, so the
 * admin confirms against the real impact rather than a description of it. The
 * same planner runs again on apply, so what is shown is what executes.
 */
function RemediationConfirm({ request, onCancel, onApplied, authenticatedFetch }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  const { upc, action } = request || {};

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/final-verification/remediation/preview`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ upc, action }),
          }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (upc && action) load();
    return () => { cancelled = true; };
  }, [upc, action, authenticatedFetch]);

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/remediation/apply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ upc, action }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
      onApplied(data);
    } catch (err) {
      setError(err.message);
      setApplying(false);
    }
  };

  if (!request) return null;
  const meta = ACTIONS[action];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{meta?.label}</h3>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{upc}</p>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <p className="text-sm text-gray-600">{meta?.blurb}</p>

          {loading ? (
            <div className="py-8 flex flex-col items-center gap-2 text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin" />
              <span className="text-sm">Working out what would change…</span>
            </div>
          ) : error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">
              {error}
            </div>
          ) : preview ? (
            <div className="space-y-3">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                This will
              </div>

              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="flex items-center gap-2 text-sm font-medium text-red-900">
                  <Trash2 className="w-4 h-4" />
                  Delete {preview.workflows_to_delete.length} output{' '}
                  {preview.workflows_to_delete.length === 1 ? 'workflow' : 'workflows'}
                </div>
                {preview.workflows_to_delete.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-0.5">
                    {preview.workflows_to_delete.map((w) => (
                      <div key={w.id} className="text-[11px] font-mono text-red-800 truncate">
                        {w.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {preview.nodes_to_reset.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                    <RotateCcw className="w-4 h-4" />
                    Reset {preview.nodes_to_reset.length} nodes to pending
                  </div>
                  <div className="mt-2 space-y-0.5">
                    {preview.nodes_to_reset.map((n) => (
                      <div key={n.node_id} className="text-[11px] text-amber-800">
                        {n.item} <span className="opacity-60">({n.current_status})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {preview.clears_ready_for_work && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
                  Mark frame <span className="font-mono text-xs">{preview.frame?.name}</span> as
                  not completed, returning it to the frame check page.
                </div>
              )}

              <div className="p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
                Kept: the frame workflow{' '}
                <span className="font-mono">{preview.frame_workflow?.name}</span>
                {action === 'segmentation_recheck' && ', including Lens Segmentation so the reviewed masks survive'}.
              </div>

              {preview.warnings?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900">
                  {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={onCancel}
            disabled={applying}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={apply}
            disabled={loading || applying || Boolean(error)}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {applying ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Applying…
              </>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Confirmation for clearing a flag raised by mistake.
 *
 * Unlike the remediations this touches a single image, and where it resumes
 * depends on which gate stopped it - so both are spelled out before confirming.
 */
function AcceptConfirm({ request, onCancel, onAccepted, authenticatedFetch }) {
  const [comment, setComment] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);

  // Held in a ref so the key handler can call the latest `accept` without being
  // rebound on every keystroke in the comment box.
  const acceptRef = useRef(null);

  useEffect(() => {
    const onKey = (event) => {
      if (!isShortcut(event, ACCEPT_KEY)) return;
      event.preventDefault();
      acceptRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // No reset effect here on purpose: the parent renders this only while there
  // is a request, so closing it unmounts the component and the next open starts
  // clean. Keeping it mounted and nulling the props is what left `applying`
  // stuck on true and the button spinning forever.
  if (!request) return null;
  const { upc, variant, variants } = request;

  // One request shape covers both scopes: a single variant, or every flagged
  // variant on the frame.
  const targets = variants || (variant ? [variant] : []);
  const wholeFrame = Boolean(variants);
  const first = targets[0];
  const source = first?.source;
  const resumesAt = source === 'ai' ? 'final human review' : 'image export';
  const mixedSources = new Set(targets.map((t) => t.source)).size > 1;

  const accept = async () => {
    setApplying(true);
    setError(null);

    const failures = [];
    let lastOk = null;
    setProgress({ done: 0, total: targets.length });

    // Sequential, and every variant is attempted even if an earlier one fails:
    // a 409 on one image (someone else got there first) should not strand the
    // rest of the frame.
    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i];
      try {
        const response = await authenticatedFetch(
          `${config.BACKEND_API_ADDRESS}/nodes/final-verification/rejected/accept`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ node_id: target.node_id, comment: comment || null }),
          }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || `HTTP ${response.status}`);
        lastOk = data;
      } catch (err) {
        failures.push({ lens_color: target.lens_color, message: err.message });
      }
      setProgress({ done: i + 1, total: targets.length });
    }

    if (!lastOk) {
      setError(targets.length === 1
        ? failures[0]?.message || 'Accept failed'
        : `None of the ${targets.length} images could be accepted. ${failures[0]?.message || ''}`);
      setApplying(false);
      setProgress(null);
      return;
    }

    onAccepted({
      ...lastOk,
      upc,
      whole_frame: wholeFrame,
      accepted_count: targets.length - failures.length,
      attempted_count: targets.length,
      failures,
    });
  };

  // Guard here rather than in the listener so a repeated press mid-flight is a
  // no-op instead of firing a second round of requests.
  acceptRef.current = applying ? null : accept;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start gap-3">
          <ThumbsUp className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">
              {wholeFrame ? 'Accept whole frame' : 'Accept image'}
            </h3>
            <p className="text-xs text-gray-500 font-mono mt-0.5 truncate">
              {upc}
              {!wholeFrame && first?.lens_color ? ` · ${first.lens_color}` : ''}
              {wholeFrame ? ` · ${targets.length} flagged variants` : ''}
            </p>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <p className="text-sm text-gray-600">
            Use this when the flag was a mistake and the{' '}
            {wholeFrame ? 'images are' : 'image is'} fine as{' '}
            {wholeFrame ? 'they are' : 'it is'}.
          </p>

          {wholeFrame ? (
            <div className="space-y-2">
              <div className="flex gap-2 flex-wrap">
                {targets.map((t) => (
                  <div key={t.node_id} className="w-16 h-16 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0" title={t.lens_color || ''}>
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.lens_color || ''} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-700">
                All <span className="font-semibold">{targets.length}</span> flagged variants of this
                frame are accepted. Variants that were never flagged are unaffected.
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-24 h-24 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0">
                {first?.image_url ? (
                  <img src={first.image_url} alt={first.lens_color || ''} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div className="text-sm text-gray-700 space-y-1">
                <div className="flex items-center gap-2">
                  <SourceBadge source={source} />
                  <span className="text-gray-500 text-xs">flagged {formatDate(first?.rejected_at)}</span>
                </div>
                <div>
                  This image only — the other variants of this frame are left alone.
                </div>
              </div>
            </div>
          )}

          <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-900">
            {mixedSources ? (
              <>
                Each image is marked as passing the gate that flagged it, and continues from there —
                AI flags resume at final human review, human flags at image export.
              </>
            ) : (
              <>
                The {wholeFrame ? 'images are' : 'image is'} marked as passing this gate and the
                workflow continues at <span className="font-medium">{resumesAt}</span>.
              </>
            )}
          </div>

          {targets.some((t) => t.source === 'human') && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
              The boxes the reviewer drew stay on the image as history; only the verdict changes.
            </div>
          )}

          {applying && progress && progress.total > 1 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
              Accepting {progress.done} of {progress.total}…
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Why (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Recorded with the override for whoever looks at this later."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-900">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={onCancel}
            disabled={applying}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={accept}
            disabled={applying}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {applying ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                {progress && progress.total > 1
                  ? `Accepting ${progress.done}/${progress.total}…`
                  : 'Accepting…'}
              </>
            ) : (
              <>
                {wholeFrame ? `Accept all ${targets.length}` : 'Accept image'}
                <Kbd>E</Kbd>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/** One finding, from either reviewer. */
function ReasonCard({ annotation, index, source }) {
  const a = annotation;
  return (
    <div className="p-3 rounded border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between gap-2">
        <ReasonChip tag={a.tag} count={1} source={source} />
        <span className="text-[10px] text-gray-400">#{index + 1}</span>
      </div>

      {/* What the AI says the rule is, before what it saw. */}
      {a.detail && a.detail !== a.comment && (
        <div className="mt-2 text-xs text-gray-500 italic">{a.detail}</div>
      )}

      {a.comment && (
        <div className="mt-2 text-xs text-gray-700 flex gap-1.5">
          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
          <span>{a.comment}</span>
        </div>
      )}

      {(a.severity || a.confidence != null) && (
        <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
          {a.severity && (
            <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 font-medium">
              {String(a.severity).replace(/_/g, ' ')}
            </span>
          )}
          {a.confidence != null && (
            <span className="flex items-center gap-1 text-gray-500">
              <Gauge className="w-3 h-3" />
              {Math.round(Number(a.confidence) * 100)}% confidence
            </span>
          )}
        </div>
      )}

      {a.reviewer && (
        <div className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
          {source === 'ai'
            ? <Sparkles className="w-3 h-3 text-gray-400" />
            : <User className="w-3 h-3 text-gray-400" />}
          {a.reviewer}
        </div>
      )}
    </div>
  );
}

function FlaggedFrameModal({ isOpen, onClose, group, onRequestAction, onRequestAccept, shortcutsPaused }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (isOpen) setIndex(0);
  }, [isOpen, group?.upc]);

  // The list can shrink under the modal when an image is accepted.
  useEffect(() => {
    const count = group?.variants?.length || 0;
    if (count && index >= count) setIndex(count - 1);
  }, [group, index]);

  useEffect(() => {
    // Paused while a confirmation sits on top, so Escape and the arrows act on
    // the dialog the user is actually looking at.
    if (!isOpen || shortcutsPaused) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (isTypingTarget(e.target)) return;
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => Math.min((group?.variants?.length || 1) - 1, i + 1));
      if (isShortcut(e, ACCEPT_KEY, { shift: true })) {
        const all = group?.variants || [];
        if (all.length) {
          e.preventDefault();
          onRequestAccept({ upc: group.upc, variants: all });
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, group, onRequestAccept, shortcutsPaused]);

  if (!isOpen || !group) return null;

  const variants = group.variants || [];
  const current = variants[index];
  const annotations = current?.annotations || [];
  const source = current?.source;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col overflow-hidden">

        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="font-mono text-sm font-semibold text-gray-900 truncate">{group.upc}</span>
              {(group.sources || []).map((s) => <SourceBadge key={s} source={s} size="xs" />)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {variants.length} flagged {variants.length === 1 ? 'variant' : 'variants'}
              {' · '}{formatDate(group.rejected_at)}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Image, with the boxes drawn on it when a human drew any */}
          <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
            <div className="flex-1 flex items-center justify-center p-6 overflow-auto">
              <button
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                disabled={index === 0}
                className="p-3 mr-3 rounded-full bg-white border border-gray-300 shadow-sm hover:bg-gray-100 disabled:opacity-30 flex-shrink-0"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>

              <AnnotatedImage
                imageUrl={current?.image_url}
                annotations={annotations}
                alt={current?.lens_color || ''}
                className="max-h-[65vh]"
              />

              <button
                onClick={() => setIndex((i) => Math.min(variants.length - 1, i + 1))}
                disabled={index >= variants.length - 1}
                className="p-3 ml-3 rounded-full bg-white border border-gray-300 shadow-sm hover:bg-gray-100 disabled:opacity-30 flex-shrink-0"
              >
                <ChevronRight className="w-6 h-6 text-gray-700" />
              </button>
            </div>

            <div className="border-t border-gray-200 bg-white px-4 py-3 flex items-center gap-2 overflow-x-auto flex-shrink-0">
              {variants.map((v, i) => (
                <button
                  key={v.node_id}
                  onClick={() => setIndex(i)}
                  title={`${v.lens_color || ''} · ${SOURCES[v.source]?.label || ''}`}
                  className={`relative flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden ${
                    i === index ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {v.image_url ? (
                    <img src={v.image_url} alt={v.lens_color || ''} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <span
                    className="absolute top-0 left-0 px-1 rounded-br text-[9px] font-semibold text-white"
                    style={{ backgroundColor: v.source === 'ai' ? AI_COLOR : '#2563eb' }}
                  >
                    {SOURCES[v.source]?.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Why it was flagged, and what to do about it */}
          <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto flex-shrink-0">
            <div className="p-4 space-y-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Variant
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium text-gray-900">
                    {current?.lens_color || 'Unknown lens colour'}
                  </div>
                  <SourceBadge source={source} size="xs" />
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {index + 1} of {variants.length} · flagged {formatDate(current?.rejected_at)}
                </div>
                <div className="text-xs text-gray-500 mt-1">{SOURCES[source]?.blurb}</div>
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Reasons ({annotations.length})
                </div>
                {annotations.length === 0 ? (
                  <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded border border-gray-200">
                    {source === 'ai'
                      ? 'Flagged without a stated reason.'
                      : 'Rejected without a marked area.'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {annotations.map((a, i) => (
                      <ReasonCard key={i} annotation={a} index={i} source={source} />
                    ))}
                  </div>
                )}
              </div>

              {current?.comment && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {source === 'ai' ? 'Reviewer summary' : 'Reviewer note'}
                  </div>
                  <div className="text-sm text-gray-700 p-3 bg-amber-50 rounded border border-amber-200">
                    {current.comment}
                  </div>
                </div>
              )}

              {/* Next step. Same options whichever gate raised the flag: accept
                  this one image, or send the whole frame back somewhere. */}
              <div className="pt-3 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Next step
                </div>

                <button
                  onClick={() => onRequestAccept({ upc: group.upc, variant: current })}
                  disabled={!current}
                  className="w-full px-2.5 py-2 text-xs font-medium rounded border border-green-300 bg-green-50 text-green-800 hover:bg-green-100 hover:border-green-400 flex items-center gap-2 text-left disabled:opacity-40"
                  title="The flag was a mistake: accept this image and let the workflow carry on."
                >
                  <ThumbsUp className="w-3.5 h-3.5 flex-shrink-0" />
                  Accept image
                  <span className="ml-auto text-[10px] font-normal opacity-70">this variant</span>
                </button>

                {variants.length > 1 && (
                  <button
                    onClick={() => onRequestAccept({ upc: group.upc, variants })}
                    className="mt-1.5 w-full px-2.5 py-2 text-xs font-medium rounded border border-green-400 bg-green-100 text-green-900 hover:bg-green-200 hover:border-green-500 flex items-center gap-2 text-left"
                    title="Every flagged image on this frame was a mistake: accept them all."
                  >
                    <ThumbsUp className="w-3.5 h-3.5 flex-shrink-0" />
                    Accept whole frame
                    <span className="ml-auto flex items-center gap-1.5 text-[10px] font-normal opacity-70">
                      all {variants.length}
                      <Kbd>⇧E</Kbd>
                    </span>
                  </button>
                )}

                <div className="mt-1.5 space-y-1.5">
                  {Object.entries(ACTIONS).map(([key, meta]) => {
                    const Icon = meta.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => onRequestAction({ upc: group.upc, action: key })}
                        className="w-full px-2.5 py-2 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 flex items-center gap-2 text-left"
                        title={meta.blurb}
                      >
                        <Icon className="w-3.5 h-3.5 flex-shrink-0 text-gray-500" />
                        {meta.label}
                        <span className="ml-auto text-[10px] font-normal text-gray-400">whole frame</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


const MODES = [
  { key: 'all', label: 'All' },
  { key: 'human', label: 'Human', Icon: User },
  { key: 'ai', label: 'AI', Icon: Sparkles },
];

function AdminReviewManager() {
  const { authenticatedFetch } = useAuth();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [source, setSource] = useState('all');
  const [counts, setCounts] = useState({ all: 0, human: 0, ai: 0 });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedUpc, setSelectedUpc] = useState(null);
  const [actionRequest, setActionRequest] = useState(null);
  const [acceptRequest, setAcceptRequest] = useState(null);
  const [actionResult, setActionResult] = useState(null);

  // Layer ownership for the keyboard: only the topmost overlay listens.
  const anyOverlayOpen = Boolean(selectedUpc || actionRequest || acceptRequest || actionResult);

  // List layer: E opens the first flagged frame.
  useEffect(() => {
    if (anyOverlayOpen) return undefined;
    const onKey = (event) => {
      if (!isShortcut(event, ACCEPT_KEY)) return;
      const first = groups[0];
      if (!first) return;
      event.preventDefault();
      setSelectedUpc(first.upc);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [anyOverlayOpen, groups]);

  // Result layer: E or Enter dismisses, so the loop returns to the list without
  // reaching for the mouse.
  useEffect(() => {
    if (!actionResult) return undefined;
    const onKey = (event) => {
      if (!isShortcut(event, ACCEPT_KEY) && !isShortcut(event, 'enter')) return;
      event.preventDefault();
      setActionResult(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actionResult]);

  const fetchFlagged = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: currentPage, page_size: pageSize, source });
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/final-verification/rejected?${params.toString()}`
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setGroups(data.items || []);
      setTotalPages(data.total_pages || 0);
      setTotalCount(data.total || 0);
      setCounts(data.counts || { all: 0, human: 0, ai: 0 });
    } catch (err) {
      console.error('Error fetching flagged frames:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, currentPage, pageSize, source]);

  useEffect(() => {
    fetchFlagged();
  }, [fetchFlagged]);

  // Track the open frame by upc, so a refresh after an action re-reads it from
  // the fresh list instead of showing a variant that is no longer flagged.
  const selected = selectedUpc ? groups.find((g) => g.upc === selectedUpc) : null;
  useEffect(() => {
    if (selectedUpc && !loading && !groups.some((g) => g.upc === selectedUpc)) {
      setSelectedUpc(null);
    }
  }, [groups, loading, selectedUpc]);

  const switchMode = (key) => {
    setSource(key);
    setCurrentPage(1);
    setSelectedUpc(null);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flagged Frames</h1>
          <p className="text-sm text-gray-600 mt-1">
            Frames stopped at a final review — by a human reviewer or by the AI reviewer — grouped by
            UPC, with the reason each was marked.
          </p>
        </div>
        <button
          onClick={fetchFlagged}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="mb-4 flex items-center gap-1 border-b border-gray-200">
        {MODES.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => switchMode(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 ${
              source === key
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {Icon && <Icon className="w-4 h-4" />}
            {label}
            <span className={`px-1.5 py-0.5 rounded-full text-[11px] ${
              source === key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
            }`}>
              {counts[key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">{error}</div>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between gap-4 flex-wrap text-sm text-gray-600">
        <div>
          {totalCount} flagged {totalCount === 1 ? 'frame' : 'frames'}
          {source !== 'all' && ` · ${SOURCES[source].label.toLowerCase()} review only`}
        </div>
        {groups.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Kbd>E</Kbd> open first
            <span className="text-gray-300">·</span>
            <Kbd>⇧E</Kbd> accept frame
            <span className="text-gray-300">·</span>
            <Kbd>E</Kbd> confirm
          </div>
        )}
      </div>

      {loading && groups.length === 0 ? (
        <div className="py-20 flex flex-col items-center gap-3 text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin" />
          <span className="text-sm">Loading flagged frames…</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="py-20 flex flex-col items-center gap-3 text-gray-500">
          <CheckCircle className="w-10 h-10 text-green-500" />
          <span className="text-sm">No flagged frames.</span>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <div
              key={group.upc}
              className="border border-gray-200 rounded-lg bg-white p-4 hover:border-red-300 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 flex-shrink-0">
                  {group.variants.slice(0, 5).map((v) => (
                    <div
                      key={v.node_id}
                      className="w-20 h-20 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center relative"
                      title={`${v.lens_color || ''} · ${SOURCES[v.source]?.label || ''}`}
                    >
                      {v.image_url ? (
                        <img src={v.image_url} alt={v.lens_color || ''} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-gray-400" />
                      )}
                      <span
                        className="absolute top-0 left-0 px-1 rounded-br text-[9px] font-semibold text-white"
                        style={{ backgroundColor: v.source === 'ai' ? AI_COLOR : '#2563eb' }}
                      >
                        {SOURCES[v.source]?.label}
                      </span>
                      {v.annotations?.length > 0 && (
                        <span className="absolute bottom-0 right-0 bg-red-600 text-white text-[10px] px-1 rounded-tl">
                          {v.annotations.length}
                        </span>
                      )}
                    </div>
                  ))}
                  {group.variant_count > 5 && (
                    <div className="w-20 h-20 rounded border border-dashed border-gray-300 flex items-center justify-center text-sm text-gray-500 font-medium">
                      +{group.variant_count - 5}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-sm font-semibold text-gray-900 truncate">
                      {group.upc}
                    </div>
                    {(group.sources || []).map((s) => <SourceBadge key={s} source={s} size="xs" />)}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                      <Layers className="w-3 h-3" />
                      {group.variant_count} {group.variant_count === 1 ? 'variant' : 'variants'}
                    </span>
                    {(group.reasons || []).map((r) => (
                      <ReasonChip
                        key={r.tag}
                        tag={r.tag}
                        count={r.count}
                        source={group.sources?.length === 1 ? group.sources[0] : undefined}
                      />
                    ))}
                  </div>
                  <div className="mt-1.5 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                    <span>Flagged {formatDate(group.rejected_at)}</span>
                    {(group.reviewers || []).length > 0 && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {group.reviewers.join(', ')}
                      </span>
                    )}
                    {(group.ai_models || []).length > 0 && (
                      <span className="flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {group.ai_models.join(', ')}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => setSelectedUpc(group.upc)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 flex-shrink-0"
                >
                  <Eye className="w-4 h-4" />
                  View details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1 || loading}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages || loading}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 flex items-center gap-1"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <FlaggedFrameModal
        isOpen={Boolean(selected)}
        group={selected}
        onClose={() => setSelectedUpc(null)}
        onRequestAction={setActionRequest}
        onRequestAccept={setAcceptRequest}
        shortcutsPaused={Boolean(actionRequest || acceptRequest || actionResult)}
      />

      {/* Mounted only while there is a request. Rendering these unconditionally
          and letting them return null internally kept them alive between opens,
          so `applying` stayed true after the first confirm and every later
          dialog opened with a disabled, spinning button. */}
      {actionRequest && (
        <RemediationConfirm
          request={actionRequest}
          authenticatedFetch={authenticatedFetch}
          onCancel={() => setActionRequest(null)}
          onApplied={async (result) => {
            setActionRequest(null);
            setSelectedUpc(null);
            setActionResult(result);
            await fetchFlagged();
          }}
        />
      )}

      {acceptRequest && (
        <AcceptConfirm
          request={acceptRequest}
          authenticatedFetch={authenticatedFetch}
          onCancel={() => setAcceptRequest(null)}
          onAccepted={async (result) => {
            setAcceptRequest(null);
            // A frame-wide accept empties the group, so close the viewer too.
            if (result.whole_frame) setSelectedUpc(null);
            setActionResult({ accepted: result });
            await fetchFlagged();
          }}
        />
      )}

      {actionResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                {actionResult.accepted
                  ? (actionResult.accepted.whole_frame ? 'Frame accepted' : 'Image accepted')
                  : 'Applied'}
              </h3>
            </div>
            {actionResult.accepted ? (
              <div className="space-y-1.5 text-sm text-gray-700">
                {actionResult.accepted.whole_frame ? (
                  <div>
                    <span className="font-semibold">{actionResult.accepted.accepted_count}</span>
                    {' of '}{actionResult.accepted.attempted_count} flagged{' '}
                    {actionResult.accepted.attempted_count === 1 ? 'image' : 'images'} accepted;
                    each resumes at the gate that flagged it.
                  </div>
                ) : (
                  <div>
                    The {SOURCES[actionResult.accepted.source]?.label.toLowerCase()} flag was cleared and
                    the workflow continues at{' '}
                    <span className="font-semibold">{actionResult.accepted.resumes_at}</span>.
                  </div>
                )}
                {actionResult.accepted.upc && (
                  <div className="font-mono text-xs text-gray-500">{actionResult.accepted.upc}</div>
                )}
                {actionResult.accepted.failures?.length > 0 && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 space-y-1">
                    <div className="font-semibold">
                      {actionResult.accepted.failures.length} could not be accepted:
                    </div>
                    {actionResult.accepted.failures.map((f, i) => (
                      <div key={i}>
                        {f.lens_color || 'variant'} — {f.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-1.5 text-sm text-gray-700">
                <div>
                  Deleted <span className="font-semibold">{actionResult.workflows_deleted_count}</span>{' '}
                  output {actionResult.workflows_deleted_count === 1 ? 'workflow' : 'workflows'}
                </div>
                {actionResult.nodes_reset > 0 && (
                  <div>
                    Reset <span className="font-semibold">{actionResult.nodes_reset}</span> nodes to pending
                  </div>
                )}
                {actionResult.frame_returned_to_check && (
                  <div>Frame returned to the frame check page</div>
                )}
                {actionResult.delete_errors?.length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-900">
                    {actionResult.delete_errors.length} workflow(s) failed to delete
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => setActionResult(null)}
              className="mt-5 w-full px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 flex items-center justify-center gap-2"
            >
              Done
              <Kbd>E</Kbd>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReviewManager;
